import React from "react"

import {
	Box,
	Button,
	Grid,
	Typography,
} from "@mui/material"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import {
	plural,
	Trans,
} from "@lingui/macro"
import { mdiPlusBox } from "@mdi/js"
import Icon from "@mdi/react"

import { PageSectionTitle } from "components/commons"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import {
	AssetMatchBrand, Brand,
	BrandPriority,
	PaginatedResponse,
} from "models"
import { ResearchService } from "services/research.service"
import withUseRouter, { UseRouterParams } from "services/router"

import { CreateResearchRequest, EditResearchRequest } from "../../models/priorities"
import { DeleteResearchDialog } from "./modal/delete-research"
import EditResearch from "./modal/edit-research/edit-research"
import { UpdateResearchForm } from "./modal/edit-research/form"
import { ResearchColumnOne } from "./research-columns/research-column-one"
import { ResearchColumnTwo } from "./research-columns/research-column-two"

interface ResearchComponentProps extends UseRouterParams {
	brands: Brand[]
}

interface ResearchComponentState extends MatesComponentState {
	loading: boolean
	researchListResponse: PaginatedResponse<BrandPriority> | null
	researchResults: PaginatedResponse<AssetMatchBrand> | null
	selectedResearchId: string
	selectedResearchIndex: number | null
	location: {
		latitude: number
		longitude: number
	}
	openDeleteDialog: boolean
	openEditDialog: boolean
	editDialogType: string
	createNew: boolean
	createCopy: boolean
	paginationRequest: {
		limit: number
		page: number
	}
}

class ResearchComponent extends React.Component<ResearchComponentProps, ResearchComponentState> {
	private readonly center = {
		latitude: 41.9028,
		longitude: 12.4964,
	} //Roma

	private readonly matesComponent: MatesComponent<ResearchComponentProps, ResearchComponentState> = new MatesComponent(this)

	constructor(props: ResearchComponentProps) {
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			loading: true,
			researchListResponse: null,
			researchResults: null,
			selectedResearchId: "",
			selectedResearchIndex: null,
			openDeleteDialog: false,
			openEditDialog: false,
			editDialogType: "",
			paginationRequest: {
				limit: 50,
				page: 0,
			},
			createCopy: false,
			createNew: false,
			location: this.center,
		}
	}

	componentDidMount() {
		this.getResearchList()
	}

	private getResearchList = () => {
		const request: { limit: number, page: number } = {
			limit: 50,
			page: 0,
		}

		ResearchService.getResearchList(request, this.getResearchListCallback, this.matesComponent.onException)
	}

	private getResearchListCallback = (response: RestResponse<PaginatedResponse<BrandPriority>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			researchListResponse: response.data,
			loading: false
		}))
	}

	private handleRowClick = (index: number) => {
		if (!this.state.researchListResponse) {
			return
		}
		this.setState(() => ({
			selectedResearchId: this.state.researchListResponse!.items[index].id,
			selectedResearchIndex: index,
		}), () => this.getResearchResults())
	}

	private getResearchResults = () => {
		if (this.state.researchListResponse === null || this.state.selectedResearchIndex === null) {
			return
		}

		this.setState(
			() => ({ loading: true, }),
			() => ResearchService.getResearchResult(
				this.state.paginationRequest,
				this.state.researchListResponse!.items[this.state.selectedResearchIndex!].id,
				this.getResearchResultCallback,
				this.matesComponent.onException
			)
		)
	}

	private getResearchResultCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrand>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			researchResults: response.data,
			loading: false,
		}))
	}


	private handleOpenClone = (index: number) => {
		this.setState(() => ({
			openEditDialog: true,
			editDialogType: "edit",
			createCopy: true
		}))
	}

	private handleCloseClone = () => {
		this.setState(() => ({
			openEditDialog: false,
			editDialogType: "",
			createCopy: false
		}))
	}

	private handleOpenEdit = () => {
		this.setState(() => ({
			openEditDialog: true,
			editDialogType: "edit"
		}))
	}

	private handleCloseEdit = () => {
		this.setState(() => ({
			openEditDialog: false,
			createNew: false
		}))
	}

	private handleOpenCreate = () => {
		this.setState(() => ({
			openEditDialog: true,
			createNew: true,
			editDialogType: "creation"
		}))
	}

	private handleOpenDelete = () => {
		this.setState(() => ({
			openDeleteDialog: true
		}))
	}

	private handleCloseDelete = () => {
		this.setState(() => ({
			openDeleteDialog: false
		}))
	}

	private editResearch = (values: any) => {
		const priorityId: string = this.state.researchListResponse!.items[this.state.selectedResearchIndex!].id
		const request: EditResearchRequest | null = UpdateResearchForm.getUpdateRequest(values, priorityId)

		const prevBrandId: string = this.state.researchListResponse!.items[this.state.selectedResearchIndex!].brand.id

		this.handleCloseEdit()
		if (request) {
			ResearchService.editResearch(prevBrandId, request, this.getResearchList, this.matesComponent.onError)
		}
	}

	private createResearch = (values: UpdateResearchForm) => {
		const request: CreateResearchRequest | null = UpdateResearchForm.getCreateRequest(values)

		this.handleCloseEdit()
		if (request) {
			ResearchService.createResearch(request, this.getResearchList, this.matesComponent.onError)
		}
	}

	private deleteResearch = (priorityId: string) => {
		this.handleCloseDelete()
		if (priorityId === this.state.selectedResearchId) {
			this.setState(() => ({
				selectedResearchId: "",
				selectedResearchIndex: null
			}), () => ResearchService.deleteResearch(priorityId, this.getResearchList, this.matesComponent.onError))
			return
		}
		ResearchService.deleteResearch(priorityId, this.getResearchList, this.matesComponent.onError)
	}

	render() {
		return (
			<>
				<Loading show={this.state.loading} />
				{this.state.researchListResponse &&
					<Box sx={{ bgcolor: "background.default" }}>
						<PageSectionTitle title={plural(
							this.state.researchListResponse.items.length,
							{
								one: `# Apertura salvata`,
								other: `# Aperture salvate`,
							}
						)}>
							<Button
								startIcon={<Icon path={mdiPlusBox} size={1} />}
								variant="contained"
								sx={{
									color: "background.default",
									backgroundColor: "secondary.main",
									textTransform: "none",
								}}
								size="small"
								disableElevation
								onClick={this.handleOpenCreate}
							>
								<Typography>
									<Trans id="research.new-opening">
										Nuova Apertura
									</Trans>
								</Typography>
							</Button>
						</PageSectionTitle>

						<Grid container height={"69vh"} alignItems={"start"} columnSpacing={3}>
							<Grid item sm={12} md={4}>
								<ResearchColumnOne
									researchData={this.state.researchListResponse}
									handleRowClick={this.handleRowClick}
									selectedResearchId={this.state.selectedResearchId}
									handleClone={this.handleOpenClone}
									handleOpenEdit={this.handleOpenEdit}
									handleOpenDelete={this.handleOpenDelete}
								/>
							</Grid>
							<Grid item sm={12} md={8} sx={{ px: 2 }}>
								{
									this.state.selectedResearchIndex !== null &&
									<ResearchColumnTwo
										researchData={this.state.researchListResponse.items[this.state.selectedResearchIndex]} />
								}
							</Grid>
							{/*<Grid item sm={12} md={4}>
								{this.state.selectedResearchIndex !== null && this.state.researchResults &&
									<ResearchColumnThree
										researchResult={this.state.researchResults.items}
										location={this.state.location} />
								}
							</Grid>*/}
						</Grid>
					</Box>
				}

				{this.state.researchListResponse && this.state.selectedResearchIndex !== null ?
					<DeleteResearchDialog open={this.state.openDeleteDialog}
						onClose={this.handleCloseDelete}
						onClick={this.deleteResearch}
						data={this.state.researchListResponse.items[this.state.selectedResearchIndex]}
					/> : null
				}
				{this.state.researchListResponse && this.state.selectedResearchIndex !== null && this.state.openEditDialog && !this.state.createCopy ?
					<EditResearch open={this.state.openEditDialog}
						onClose={this.handleCloseEdit}
						data={this.state.researchListResponse.items[this.state.selectedResearchIndex]}
						type={this.state.editDialogType}
						submit={this.editResearch}
						brands={this.props.brands}
					/> : null
				}
				{this.state.researchListResponse && this.state.createNew ?
					<EditResearch open={this.state.openEditDialog}
						onClose={this.handleCloseEdit}
						data={null}
						type={this.state.editDialogType}
						submit={this.createResearch}
						brands={this.props.brands}
					/> : null
				}
				{
					this.state.researchListResponse && this.state.selectedResearchIndex !== null && this.state.createCopy ?
						<EditResearch open={this.state.openEditDialog}
							onClose={this.handleCloseClone}
							data={this.state.researchListResponse.items[this.state.selectedResearchIndex]}
							type={this.state.editDialogType}
							submit={this.createResearch}
							brands={this.props.brands}
							duplicate
						/> : null
				}
			</>

		)
	}
}

export default withUseRouter(ResearchComponent)
